export const CONTACT = {
  phone: "+91-8744807237",
  phoneHref: "+918744807237",
  email: "contactus@coderealm.codes",
  location: "India — serving clients worldwide",
};

export const SERVICES = [
  {
    id: "ai-development",
    index: "01",
    title: "AI Development",
    tag: "Next-Gen AI",
    description:
      "Custom LLM fine-tuning, RAG architectures, autonomous agent swarms and vision AI pipelines — engineered for production, not demos.",
    chips: ["LLM Fine-tuning", "RAG Systems", "Agent Swarms", "Vision AI"],
  },
  {
    id: "n8n-automation",
    index: "02",
    title: "n8n Automation",
    tag: "Hyper-Efficiency",
    description:
      "Enterprise workflow orchestration, zero-latency API integrations, ETL pipelines and event-driven systems that run while you sleep.",
    chips: ["Workflow Orchestration", "API Integrations", "ETL Pipelines", "Event-Driven"],
  },
  {
    id: "react-fullstack",
    index: "03",
    title: "React Full-Stack",
    tag: "High-Performance",
    description:
      "Ultra-fast web applications, real-time analytics dashboards, cloud architecture and microfrontends that hold 60 FPS under pressure.",
    chips: ["React 19", "Real-Time Dashboards", "Cloud Architecture", "Microfrontends"],
  },
];

export const CASE_STUDIES = [
  {
    id: "hospitality-audit-dashboards",
    category: "React Full-Stack",
    title: "Hospitality Quality Audit",
    subtitle: "Multi-tenant hotel dashboards for assessment standing",
    client: "Anonymous",
    industry: "Hospitality",
    timeline: "",
    year: "2025",
    summary:
      "For a firm that audits five-star hotels worldwide, we shipped the hotel-facing product layer: multi-tenant dashboards so brands and properties can see assessment standing, peer context, and day-to-day outcomes — without waiting on PDF report cycles.",
    metrics: [],
    tags: ["React", "Multi-tenant", "Role-based access", "Assessment dashboards", "Benchmarking"],
    image:
      "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxob3RlbCUyMGx1eHVyeSUyMGRhc2hib2FyZCUyMGFzc2Vzc21lbnR8ZW58MHx8fHwxNzg4Mjc5ODgzfDA&ixlib=rb-4.1.0&q=85&w=1600",
    challenge:
      "Auditors had strong back-office assessment workflows, but hotel brands and properties had almost no live visibility. Standing against standards and peers lived in static reports. Ops and quality teams could not answer 'where do we stand this week?' without chasing the assessment firm. The gap was a client-facing product surface: auth, tenancy, and dashboards that mapped assessment data into something hotels could act on.",
    solution:
      "We built the production React client for hotel users on the live multi-tenant stack: role-aware views, property/brand scoping, and dashboards for standing, benchmarks, and assessment outcomes. Hotels see how they compare to standards and peers in the product itself — not only in a dumped report. Architecture stayed aligned with the existing assessment platform (tenant isolation, assessment entities, dashboard read models) so the client layer shipped into a real production system rather than a throwaway prototype.",
    architecture: [
      "React production SPA (hotel / brand client)",
      "Multi-tenant tenancy + role-based access",
      "Assessment standing & peer benchmarking views",
      "Dashboard read models over live assessment data",
      "Property / brand scoped navigation",
    ],
  },
  {
    id: "financial-parser-dashboards",
    category: "Parser + React Full-Stack",
    title: "Financial Investment Parser",
    subtitle: "Balance-sheet ingest → normalized data → analyst dashboards",
    client: "Anonymous",
    industry: "Finance",
    timeline: "", 
    year: "2025",
    summary:
      "For a financial investment firm, we shipped a two-tier workflow: upload balance sheets and related statements, parse and normalize the numbers, then feed custom React dashboards for company performance and projections — so analysts stop living in spreadsheet handoffs.",
    metrics: [],
    tags: ["Document ingest", "Parser", "Data normalization", "React dashboards", "Investment ops"],
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBkYXNoYm9hcmQlMjBpbnZlc3RtZW50JTIwYW5hbHl0aWNzfGVufDB8fHx8MTc4ODI3OTg4M3ww&ixlib=rb-4.1.0&q=85&w=1600", 
    challenge:
      "Analysts moved statements through manual spreadsheet pipelines: upload lived in email or shared drives, parsing was inconsistent, and every company dashboard was a one-off workbook. Errors crept in at handoff boundaries. The firm needed a product path — ingest → structured data → reusable dashboards — not another template.",
    solution:
      "We built the upload and parser path to turn balance-sheet inputs into normalized, queryable company data, then the dashboard layer analysts use to track performance and projections. Parsing and normalization sit in the middle so UI is not hard-coded to raw spreadsheet layouts. The React dashboards consume the cleaned model, which keeps company views consistent as new statements land.",
    architecture: [
      "Secure document upload for balance sheets / statements",
      "Balance-sheet parser + field mapping",
      "Normalization layer into a company data model",
      "Custom React dashboards (performance & projections)",
      "Analyst workflow without spreadsheet handoffs",
    ],
  },
  {
    id: "hospitality-knowledge-agent",
    category: "AI / Agents",
    title: "Hospitality Knowledge Agent",
    subtitle: "Company-wide AI that connects notes, code, DB, and PRs",
    client: "Anonymous",
    industry: "Hospitality",
    timeline: "",
    year: "2025",
    summary:
      "For a hospitality company, we built a company-wide knowledge agent: Obsidian-style graph memory over meeting notes, codebase, and database context — with skills that answer grounded questions, review PRs, flag security issues, join meetings when needed, and suggest optimizations that connect the dots across ops and engineering.",
    metrics: [],
    tags: ["LLM agents", "Obsidian graph memory", "PR review", "Security checks", "Meeting notes", "DB grounding"],
    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxBSSUyMGFnZW50JTIwa25vd2xlZGdlJTIwZ3JhcGglMjBob3NwaXRhbGl0eXxlbnwwfHx8fDE3ODgyNzk4ODN8MA&ixlib=rb-4.1.0&q=85&w=1600",
    challenge:
      "Knowledge lived in silos — meeting notes in one place, code and PRs in another, production data in the DB. People spent hours reconstructing 'why we did this' or 'is this safe to ship.' Answers were tribal, reviews missed context, and nobody had a single agent that could reason across notes, repos, and data without hallucinating past the source of truth.",
    solution:
      "We shipped a hospitality knowledge agent with a skill pack on top of graphical (Obsidian) memory. The agent grounds answers in meeting notes, the codebase, and DB checks; reviews PRs; runs security vulnerability checks; joins meetings when required; and proposes optimized next steps that link those sources. Instead of another chatbot, it behaves like an ops teammate with explicit skills — query, review, secure, attend, connect, suggest.",
    architecture: [
      "Obsidian-style graphical memory (notes ↔ entities ↔ links)",
      "LLM agent runtime with discrete skills",
      "Grounded Q&A over notes + codebase + DB read paths",
      "PR review + security vulnerability checks",
      "Meeting attendance / notes ingest when required",
      "Optimization suggestions that cross-link sources",
    ],
  },
  {
    id: "retail-pos-inventory-ops",
    category: "React Full-Stack",
    title: "Retail POS & Ops Platform",
    subtitle: "Inventory, purchases, invoices, and automated financial reports",
    client: "Anonymous",
    industry: "Retail",
    timeline: "",
    year: "2025",
    summary:
      "For a retail brand, we built a POS that holds inventory and manages purchases and invoices — then automated tracking, financial communications, and on-demand custom reports so ops isn't stuck in spreadsheets.",
    metrics: [],
    tags: ["POS", "Inventory", "Purchases & invoices", "Financial automation", "Custom reports", "React"],
    image:
      "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxyZXRhaWwlMjBQT1MlMjBpbnZlbnRvcnklMjBzeXN0ZW18ZW58MHx8fHwxNzg4Mjc5ODgzfDA&ixlib=rb-4.1.0&q=85&w=1600",
    challenge:
      "Selling, stocking, and settling lived in separate tools and handoffs. Inventory drifted from purchases; invoices and financial follow-ups were manual; reports were rebuilt whenever someone asked. The team needed one product path from sale → stock → invoice → communication → report, with automation when the user wants it.",
    solution:
      "We shipped a React POS with inventory and purchase/invoice management at the core, then automation for tracking details and financial communications, plus custom report generation users can trigger (and schedule/automate when required). One system owns the operational loop instead of a pile of workbooks and ad-hoc messages.",
    architecture: [
      "POS sell flow",
      "Inventory holding & stock updates",
      "Purchase and invoice management",
      "Automated tracking + financial communications",
      "User-triggered (and automatable) custom reports",
    ],
  },
];

export const MANIFESTO = [
  {
    index: "01",
    title: "Mission",
    body: "Every system we ship exists to remove friction from someone's workday. If it doesn't move a metric that matters, it doesn't leave our studio.",
  },
  {
    index: "02",
    title: "Precision",
    body: "We sweat the invisible details — retry queues, guardrails, render budgets — because reliability is the only feature users never notice until it's gone.",
  },
  {
    index: "03",
    title: "Speed",
    body: "Weeks, not quarters. Small senior teams, sharp scopes and relentless iteration get working software into your hands while the idea is still hot.",
  },
];
